import { AccountType, PartyRecordType } from '@xbcb/shared-types';
import { FinancialOnboardingStatus } from '@xbcb/party-types';
import { pascalCase } from 'change-case';
import {
  checkAccess,
  UserPermissions,
} from '@xbcb/client-utils/dist/checkAccess';
import { executeMutation } from 'libs/executeMutation';
import {
  RetryForwarderFinanceOnboardingDocument,
  RetryShipperFinanceOnboardingDocument,
  type RetryForwarderFinanceOnboardingMutationVariables,
  type RetryShipperFinanceOnboardingMutationVariables,
} from '__generated__/graphql';
import { AppRecordKebabMenuItem } from '../../types';

type RetryableRecordType = PartyRecordType.SHIPPER | PartyRecordType.FORWARDER;

// Shipper and forwarder take distinct Update*Input types, so each party type
// gets its own operation; the request body is identical for both.
const mutations = {
  [PartyRecordType.SHIPPER]: RetryShipperFinanceOnboardingDocument,
  [PartyRecordType.FORWARDER]: RetryForwarderFinanceOnboardingDocument,
} as const satisfies Record<RetryableRecordType, unknown>;

// Sets financeOnboardingRequired=true; the next party event re-triggers because
// FAILED is a retriggerable status in PW's handleFinanceOnboarding.
export const appRecordRetryFinanceOnboardingMenuItem = (
  recordType: RetryableRecordType,
): AppRecordKebabMenuItem => ({
  key: `retry${pascalCase(recordType)}FinanceOnboarding`,
  text: 'Retry Finance Onboarding',
  show: ({ currentUser, existingRecord }) =>
    existingRecord?.financeOnboardingDetails?.status ===
      FinancialOnboardingStatus.FAILED &&
    currentUser?.accountType === AccountType.OPERATOR &&
    checkAccess(currentUser, recordType, UserPermissions.UPDATE),
  action: async ({ record }) => {
    const variables:
      | RetryShipperFinanceOnboardingMutationVariables
      | RetryForwarderFinanceOnboardingMutationVariables = {
      id: record.id,
      version: record.version,
      input: {
        financeOnboardingDetails: {
          financeOnboardingRequired: true,
        },
      },
    };

    await executeMutation({ mutation: mutations[recordType], variables });
  },
  confirmAction: true,
});
